import * as React from "react"
import { cn } from "@/lib/utils"
import { Textarea } from "@/components/ui/textarea" // Import the base Textarea
import { MarkdownRenderer } from "@/components/ui/markdown-renderer" // Import MarkdownRenderer
import { ModeToggle, type MarkdownMode } from "@/components/ui/mode-toggle" // Import ModeToggle
import { SplitView } from "@/components/ui/split-view" // Import SplitView

// Extend Textarea props to include markdown support and mode switching
interface EditableTextareaProps extends React.ComponentProps<typeof Textarea> {
  readOnly?: boolean;
  markdown?: boolean; // Enable markdown rendering
  markdownMode?: MarkdownMode; // Current editing mode
  showModeToggle?: boolean; // Show mode switcher
  onModeChange?: (mode: MarkdownMode) => void; // Mode change callback
  minHeight?: string; // Custom minimum height (defaults to "min-h-[300px] md:min-h-[400px]")
  maxHeight?: string; // Custom maximum height (defaults to "max-h-[600px] md:max-h-[800px]")
}

const EditableTextarea = React.forwardRef<
  HTMLTextAreaElement,
  EditableTextareaProps
>(
  ({ className, readOnly, markdown, markdownMode = 'edit', showModeToggle = false, onModeChange, minHeight = "min-h-[300px] md:min-h-[400px]", maxHeight = "max-h-[600px] md:max-h-[800px]", onChange, ...props }, ref) => {
    const [internalMode, setInternalMode] = React.useState<MarkdownMode>(markdownMode);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    
    // Use provided ref or internal ref
    const textareaElementRef = ref || textareaRef;

    // Auto-resize function
    const autoResize = React.useCallback((textarea: HTMLTextAreaElement) => {
      if (!textarea) return;
      
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Set height to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, []);

    // Effect to handle auto-resize when content changes
    React.useEffect(() => {
      const textarea = textareaElementRef && 'current' in textareaElementRef ? textareaElementRef.current : null;
      if (textarea && !readOnly) {
        autoResize(textarea);
      }
    }, [props.value, autoResize, textareaElementRef, readOnly]);

    // Sync external mode changes
    React.useEffect(() => {
      setInternalMode(markdownMode);
    }, [markdownMode]);

    // Handle mode changes
    const handleModeChange = React.useCallback((newMode: MarkdownMode) => {
      setInternalMode(newMode);
      onModeChange?.(newMode);
      
      // Focus textarea when switching to edit mode
      if (newMode === 'edit' && textareaElementRef && 'current' in textareaElementRef) {
        setTimeout(() => {
          const textarea = textareaElementRef.current;
          textarea?.focus();
          // Trigger auto-resize after focus
          if (textarea) {
            autoResize(textarea);
          }
        }, 0);
      }
    }, [onModeChange, textareaElementRef, autoResize]);

    // Handle input changes with auto-resize
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Call the original onChange if provided
      onChange?.(e);
      
      // Auto-resize the textarea
      autoResize(e.target);
    }, [onChange, autoResize]);

    // Keyboard shortcuts
    React.useEffect(() => {
      if (!markdown || !showModeToggle) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey) {
          switch (event.key) {
            case 'E':
              event.preventDefault();
              handleModeChange('edit');
              break;
            case 'P':
              event.preventDefault();
              handleModeChange('preview');
              break;
            case 'S':
              event.preventDefault();
              handleModeChange('split');
              break;
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [markdown, showModeToggle, handleModeChange]);

    const currentMode = internalMode;
    const displayValue = String(props.value ?? props.defaultValue ?? "");

    // Helper function to render content based on mode
    const renderContent = () => {
      // When in readOnly mode, render display content
      if (readOnly) {
        // If markdown is enabled and we have content, render as markdown
        if (markdown && displayValue) {
          return (
            <MarkdownRenderer
              content={displayValue}
              className="w-full"
            />
          );
        }
        
        // Default readOnly rendering for non-markdown or empty content
        return (
          <div className="w-full whitespace-pre-wrap break-words break-all">
            {displayValue !== "" ? (
              displayValue
            ) : (
              <span className="text-gray-400">{props.placeholder}</span>
            )}
          </div>
        );
      }

      // Editable mode with markdown support
      if (markdown && showModeToggle) {
        switch (currentMode) {
          case 'preview':
            return (
              <>
                {displayValue ? (
                  <MarkdownRenderer content={displayValue} className="w-full" />
                ) : (
                  <span className="text-gray-400">{props.placeholder}</span>
                )}
              </>
            );
          
          case 'split':
            // Split view - side by side edit and preview
            return (
              <SplitView
                minHeight={!readOnly ? minHeight : undefined}
                maxHeight={!readOnly ? maxHeight : undefined}
                editPanel={
                  <Textarea
                    ref={textareaElementRef}
                    className={cn(
                      "w-full flex-1 bg-transparent", // Use flex-1 instead of !h-full to properly fill container
                      "text-base text-foreground placeholder-gray-400", // Base text styling
                      "!border-0 outline-none", // Override all borders
                      "focus:outline-none focus:ring-0 focus:!border-0", // Remove focus styling
                      "disabled:cursor-not-allowed disabled:opacity-100",
                      "resize-none !px-4 !py-4 !min-h-0 overflow-y-auto" // Override padding, min-height, and handle overflow
                    )}
                    onChange={handleChange}
                    {...props}
                  />
                }
                previewPanel={
                  <div className="pl-6 pr-6 py-4 flex-1 min-h-0 overflow-y-auto">
                    {displayValue ? (
                      <MarkdownRenderer content={displayValue} className="w-full" />
                    ) : (
                      <span className="text-gray-400">{props.placeholder}</span>
                    )}
                  </div>
                }
              />
            );
          
          case 'edit':
          default:
            return (
              <Textarea
                ref={textareaElementRef}
                className={cn(
                  "flex w-full bg-transparent", // Keep bg transparent, flex, width
                  "text-base p-3", // Add mt-2 for consistent spacing with other modes
                  "border border-gray-300", // Use specific gray border color
                  "focus-visible:outline-none focus:border-gray-300", // Remove focus ring effects
                  "disabled:cursor-not-allowed", // Base disabled style (opacity comes from Textarea)
                  "disabled:opacity-100", // Override base opacity
                  !readOnly && minHeight, // Only apply minimum height when not readonly
                  !readOnly && maxHeight, // Only apply maximum height when not readonly
                  "resize-none overflow-y-auto" // Auto-grow with scroll when needed
                )}
                onChange={handleChange}
                {...props}
              />
            );
        }
      }

      // Standard editable mode (no markdown mode switching)
      return (
        <Textarea
          ref={textareaElementRef}
          readOnly={readOnly}
          className={cn(
            // Use specific gray border color, but only when not readonly
            readOnly ? "border-0" : "border border-gray-300",
            "flex w-full bg-transparent", // Keep bg transparent, flex, width
            "text-base p-3 mt-2", // Consistent padding and spacing
            "focus-visible:border-gray-300", // Remove focus ring effects
            "disabled:cursor-not-allowed", // Base disabled style (opacity comes from Textarea)
            "disabled:opacity-100", // Override base opacity
            !readOnly && minHeight, // Only apply minimum height when not readonly
            !readOnly && maxHeight, // Only apply maximum height when not readonly
            "resize-none overflow-y-auto" // Auto-grow with scroll when needed
          )}
          onChange={handleChange}
          {...props}
        />
      );
    };

    const shouldShowAsContainer = readOnly || (markdown && showModeToggle && currentMode === 'preview');
    const shouldUseSplitView = markdown && showModeToggle && currentMode === 'split';
    const handleClick = shouldShowAsContainer && currentMode === 'preview' ? () => handleModeChange('edit') : props.onClick;

    return (
      <div className="w-full">
        {/* Mode Toggle - Show when markdown is enabled and showModeToggle is true */}
        {markdown && showModeToggle && !readOnly && (
          <div className="flex justify-end">
            <ModeToggle
              key={`mode-toggle-${currentMode}`}
              mode={currentMode}
              onModeChange={handleModeChange}
              showSplit={true} // Enable split mode in Phase 3
            />
          </div>
        )}
        
        {/* Main Content Area */}
        {shouldUseSplitView ? (
          // Split view renders its own container with proper ID
          renderContent()
        ) : shouldShowAsContainer ? (
          <div
            className={cn(
              "flex w-full bg-transparent", // base layout
              // Only apply horizontal padding when not readonly, always apply vertical padding
              readOnly ? "text-base py-3" : "text-base p-3",
              // No border in readonly mode
              readOnly ? "" : "border border-gray-300", // Only add border when not readonly
              "cursor-pointer", // indicate clickability
              !readOnly && minHeight, // Only apply minimum height when not readonly
              !readOnly && maxHeight, // Only apply maximum height when not readonly
              "overflow-y-auto", // Add scroll when content exceeds max height
              className
            )}
            onClick={handleClick as unknown as React.MouseEventHandler<HTMLDivElement>}
            id="markdown-content"
          >
            {renderContent()}
          </div>
        ) : (
          <div 
            className={cn(
              // Remove borders for standard mode - parent components handle borders
              "border-0", // No border - parent container handles borders  
              className
            )}
            id="markdown-content"
          >
            {renderContent()}
          </div>
        )}
        
        {/* Hidden input for forms in readOnly mode */}
        {readOnly && (
          <input type="hidden" readOnly value={displayValue} />
        )}
      </div>
    );
  }
);
EditableTextarea.displayName = "EditableTextarea";

export { EditableTextarea, type MarkdownMode };
